import { useEffect } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { ToggleField } from '@brighte/form-components';
import { HouseholdType, Period } from '@brighte/validation';
import { RepaymentField } from '@components/RepaymentField';
import { Stack } from '@spark-web/stack';
import { FinancialFieldTypes, HOUSEHOLD } from '@utils';

type Props = Pick<UseFormReturn<HouseholdType>, 'watch' | 'control' | 'setValue'>;

function Rent({ control, watch, setValue }: Props) {
  const { RENT_LABEL } = HOUSEHOLD;

  const RENT_FIELD = 'applicant.expense.rent' as FieldPath<HouseholdType>;
  const MORTGAGE_FIELD = 'applicant.expense.mortgage' as FieldPath<HouseholdType>;

  const [isRentNotApplicable, applicantRentAmount, applicantRentPeriod, isMortgageNotApplicable] = watch([
    `${RENT_FIELD}.isNotApplicable`,
    `${RENT_FIELD}.amount`,
    `${RENT_FIELD}.period`,
    `${MORTGAGE_FIELD}.isNotApplicable`,
  ]);

  useEffect(() => {
    if (!isMortgageNotApplicable) {
      setValue(`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>, true);
    } else {
      setValue(`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>, isRentNotApplicable ?? true);
    }
  }, [isMortgageNotApplicable, isRentNotApplicable]);

  const handleRentToggle = (value: boolean) => {
    // value is isNotApplicable: true = "No" (not applicable), false = "Yes" (has rent)
    if (value) {
      setValue(`${RENT_FIELD}.amount` as FieldPath<HouseholdType>, 0);
      setValue(`${RENT_FIELD}.period` as FieldPath<HouseholdType>, Period.Monthly);
    }
  };

  return (
    isMortgageNotApplicable && (
      <Stack gap="xlarge">
        <ToggleField
          name={`${RENT_FIELD}.isNotApplicable` as FieldPath<HouseholdType>}
          label={RENT_LABEL}
          control={control}
          layout="inline"
          onChange={handleRentToggle}
          isInvertedValue
        />
        {!isRentNotApplicable && isRentNotApplicable !== undefined && (
          <RepaymentField
            control={control}
            periodFieldName={`${RENT_FIELD}.period` as FieldPath<HouseholdType>}
            amountFieldName={`${RENT_FIELD}.amount` as FieldPath<HouseholdType>}
            periodValue={applicantRentPeriod}
            amountValue={applicantRentAmount}
            financialFieldType={FinancialFieldTypes.RENT}
            heading="Rent Payments"
          />
        )}
      </Stack>
    )
  );
}

export default Rent;
